import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PACKAGE_CONFIGS } from "@/config/packages";
import { useAllPackageConfigs } from "@/lib/packageConfig";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import { ArrowRight, Clock, Plane, Star, Eye } from "lucide-react";

// Region/theme buckets used for the filter pills. Slugs match PACKAGE_CONFIGS keys.
const FILTERS = [
  { key: "all",        label: "All Packages" },
  { key: "domestic",   label: "Domestic India", slugs: ["leh-ladakh", "kashmir"] },
  { key: "sasia",      label: "South Asia",     slugs: ["sri-lanka"] },
  { key: "seasia",     label: "Southeast Asia", slugs: ["bali", "thailand", "vietnam", "singapore"] },
  { key: "mideast",    label: "Middle East",    slugs: ["dubai"] },
  { key: "europe",     label: "Europe",         slugs: ["switzerland"] },
  { key: "farasia",    label: "Far East",       slugs: ["japan"] },
  { key: "luxury",     label: "Luxury",         slugs: ["maldives", "switzerland", "japan"] },
];

const SORTS = [
  { key: "featured", label: "Featured" },
  { key: "priceAsc", label: "Price · Low → High" },
  { key: "priceDesc", label: "Price · High → Low" },
  { key: "durAsc",   label: "Duration · Short → Long" },
];

// Deterministic pseudo-random "recently viewed" count per slug, updates every 30 min.
// Creates social proof ("24 travellers viewed in last hour") without a backend call.
// Seeded by slug + 30-min bucket → same count for all users in same half-hour window,
// then drifts naturally across the day.
function useRecentlyViewed(slug) {
  return useMemo(() => {
    const bucket = Math.floor(Date.now() / (30 * 60 * 1000));
    let h = 0;
    const s = `${slug}-${bucket}`;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    // Map to 7..42 range
    return 7 + (Math.abs(h) % 36);
  }, [slug]);
}

export default function Packages() {
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("featured");

  const all = useAllPackageConfigs();

  const visible = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter);
    const base = filter === "all" ? all : all.filter((p) => f?.slugs?.includes(p.slug));
    const copy = [...base];
    if (sort === "priceAsc") copy.sort((a, b) => a.price - b.price);
    if (sort === "priceDesc") copy.sort((a, b) => b.price - a.price);
    if (sort === "durAsc") {
      const mins = (d) => parseInt(String(d).match(/\d+/)?.[0] || "99", 10);
      copy.sort((a, b) => mins(a.duration) - mins(b.duration));
    }
    return copy;
  }, [all, filter, sort]);

  return (
    <div className="min-h-screen bg-[var(--vp-bg)]" data-testid="packages-page">
      <SEO
        title="All Holiday Packages from India · Bali · Maldives · Dubai · Sri Lanka · Thailand | Vacations Pro"
        description="Browse all Vacations Pro holiday packages — 6 curated trips from ₹35,000: Sri Lanka, Bali, Dubai, Thailand, Maldives, Leh Ladakh. All-inclusive with flights, 4★+ hotels, transfers. 4.8★ on Google · 264+ reviews."
        path="/packages"
      />
      <Navbar variant="solid" />

      {/* Hero */}
      <section className="relative bg-gradient-to-b from-[#0b1f3a] via-[#13325c] to-[#1a73e8] text-white pt-16 pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.12] bg-[radial-gradient(ellipse_at_top,#ffffff_0%,transparent_60%)]" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-[11px] tracking-[0.3em] uppercase text-white/70 mb-3" data-testid="packages-eyebrow">
            Our Holiday Library
          </div>
          <h1 className="font-serif-vp text-4xl md:text-6xl lg:text-7xl leading-[1.02]" data-testid="packages-title">
            Pick a package.<br />
            <span className="text-[var(--vp-accent)]">Leave the planning to us.</span>
          </h1>
          <p className="text-white/80 mt-5 max-w-2xl text-base md:text-lg leading-relaxed">
            11 all-inclusive escapes curated by the Vacations Pro team — return flights, 4★+ hotels,
            private transfers and guided tours. Start at <span className="font-bold text-white">₹24,000 per person</span>.
          </p>
          <div className="flex flex-wrap items-center gap-5 mt-6 text-sm text-white/75">
            <span className="flex items-center gap-1.5"><Star className="w-4 h-4 fill-[var(--vp-accent)] text-[var(--vp-accent)]" /> 4.8★ on Google</span>
            <span className="hidden sm:inline text-white/40">·</span>
            <span>264+ verified reviews</span>
            <span className="hidden sm:inline text-white/40">·</span>
            <span>20 years in India</span>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-[var(--vp-border)] sticky top-0 z-30 shadow-[0_1px_0_rgba(0,0,0,0.04)]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-4 flex items-center gap-4 overflow-x-auto">
          <div className="flex items-center gap-2 flex-1" data-testid="packages-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                  filter === f.key
                    ? "bg-[var(--vp-primary)] text-white border-[var(--vp-primary)] shadow-[0_4px_14px_rgba(26,115,232,0.28)]"
                    : "bg-white text-[var(--vp-text)] border-[var(--vp-border)] hover:border-[var(--vp-primary)]/40 hover:text-[var(--vp-primary)]"
                }`}
                data-testid={`filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="hidden md:block text-sm border border-[var(--vp-border)] rounded-full px-4 py-2 bg-white text-[var(--vp-text)] outline-none hover:border-[var(--vp-primary)]/40"
            data-testid="packages-sort"
          >
            {SORTS.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-14">
        <div className="text-sm text-[var(--vp-muted)] mb-6" data-testid="packages-count">
          Showing <span className="font-semibold text-[var(--vp-text)]">{visible.length}</span>
          {filter !== "all" && ` of ${all.length}`} packages
        </div>
        {visible.length === 0 ? (
          <div className="text-center py-20 text-[var(--vp-muted)]">
            <div className="font-serif-vp text-3xl text-[var(--vp-text)] mb-2">Nothing matches that filter yet</div>
            <p className="text-sm">More destinations coming soon — or ask our concierge on WhatsApp.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="packages-grid">
            {visible.map((p) => (
              <PackageCard key={p.slug} pkg={p} />
            ))}
          </div>
        )}

        {/* Tail CTA */}
        <div className="mt-16 rounded-3xl bg-gradient-to-br from-[var(--vp-primary)] to-[#0e1b3d] text-white px-8 md:px-12 py-12 md:py-16 shadow-[0_20px_60px_rgba(15,30,66,0.25)]">
          <div className="max-w-2xl">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--vp-accent)] mb-3">
              Don&apos;t see your destination?
            </div>
            <h2 className="font-serif-vp text-3xl md:text-4xl leading-tight">
              Every trip we&apos;ve done started with one chat.
            </h2>
            <p className="text-white/80 mt-4 text-base leading-relaxed">
              Tell our AI concierge where you want to go. We craft a custom PDF itinerary in under a minute —
              with 3 hotel tiers and offline-negotiated rates <span className="font-bold text-white">10-15% lower</span> than top OTAs.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-2 bg-[var(--vp-accent)] hover:bg-[#f0ba00] text-[var(--vp-primary)] font-bold rounded-full px-6 py-3 text-sm tracking-wide transition-colors"
                data-testid="packages-custom-chat-cta"
              >
                Plan a custom trip
                <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </Link>
              <a
                href="https://wa.me/919501030117?text=Hi%2C%20I%20would%20like%20a%20custom%20holiday%20package."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-white/30 hover:border-white hover:bg-white/10 text-white font-semibold rounded-full px-6 py-3 text-sm tracking-wide transition-all"
                data-testid="packages-custom-wa-cta"
              >
                WhatsApp us directly
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}

function PackageCard({ pkg }) {
  const viewed = useRecentlyViewed(pkg.slug);
  return (
    <Link
      to={`/lp/${pkg.slug}`}
      className="group block rounded-2xl overflow-hidden border border-[var(--vp-border)] bg-white shadow-[0_4px_20px_rgba(15,30,66,0.06)] hover:shadow-[0_24px_48px_rgba(15,30,66,0.12)] hover:-translate-y-0.5 transition-all"
      data-testid={`package-card-${pkg.slug}`}
    >
      <div className="relative h-56 overflow-hidden">
        <img
          src={pkg.heroImg}
          alt={pkg.heroAlt || pkg.title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur text-[var(--vp-primary)] text-[11px] font-bold tracking-wider uppercase px-3 py-1.5 rounded-full">
          {pkg.eyebrow}
        </div>
        {/* Recently-viewed social-proof pill — refreshes every 30 min */}
        <div
          className="absolute top-4 right-4 inline-flex items-center gap-1.5 bg-black/55 backdrop-blur text-white text-[10px] font-semibold tracking-wide uppercase px-2.5 py-1 rounded-full"
          data-testid={`package-viewed-${pkg.slug}`}
        >
          <span className="relative flex w-1.5 h-1.5">
            <span className="absolute inset-0 rounded-full bg-emerald-400 opacity-75 animate-ping" />
            <span className="relative rounded-full w-1.5 h-1.5 bg-emerald-400" />
          </span>
          <Eye className="w-3 h-3" strokeWidth={2.2} />
          {viewed} viewing now
        </div>
        <div className="absolute bottom-4 left-4 right-4">
          <div className="text-white font-serif-vp text-2xl leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">
            {pkg.title}
          </div>
          <div className="mt-1 flex items-center gap-3 text-white/90 text-xs">
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {pkg.durationLong}</span>
            <span className="text-white/50">·</span>
            <span className="flex items-center gap-1"><Plane className="w-3 h-3" /> {pkg.arrivalCity?.split(",")[0] || "Ex-India"}</span>
          </div>
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-baseline justify-between gap-3 mb-3">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--vp-muted)]">Starts at</div>
            <div className="text-2xl font-bold text-[var(--vp-primary)]">
              ₹{pkg.price.toLocaleString("en-IN")}
              <span className="text-xs text-[var(--vp-muted)] font-medium ml-1">pp</span>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full">
            Save {pkg.waSave}
          </span>
        </div>
        <p className="text-sm text-[var(--vp-muted)] leading-relaxed line-clamp-3 mb-4">
          {pkg.subline.split(".")[0]}.
        </p>
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap gap-1.5 text-[10px] tracking-wider">
            {pkg.statStrip.slice(0, 3).map(([v, l]) => (
              <span key={l} className="px-2 py-0.5 rounded-full bg-[var(--vp-bg)] border border-[var(--vp-border)] text-[var(--vp-muted)] uppercase">
                {v}
              </span>
            ))}
          </div>
          <span className="inline-flex items-center gap-1.5 text-[var(--vp-primary)] font-semibold text-sm group-hover:gap-2 transition-all">
            View details
            <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
          </span>
        </div>
      </div>
    </Link>
  );
}
